/**
 * Authentication Context Provider
 * Manages authentication state and provides auth-related functions throughout the app
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  AuthContextValue, 
  AuthUser, 
  AuthError,
  AuthenticationState 
} from '../Types/auth';
import { logoutConfig } from '../auth/auth0Config';

// Create the context
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Custom hook to use the authentication context
 * Throws an error if used outside of AuthProvider
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 * Wraps the app and provides authentication state management
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Auth0 hooks and state
  const { 
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    user: auth0User,
    error: auth0Error,
    getAccessTokenSilently,
    logout 
  } = useAuth0();
  
  // Local authentication state
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [unauthorizedUser, setUnauthorizedUser] = useState(false);
  const [authError, setAuthError] = useState<AuthError | null>(null);
  const [authState, setAuthState] = useState<AuthenticationState>('pending_authentication');
  
  // Ref to prevent duplicate API calls
  const isLoadingProfile = useRef(false);

  /**
   * Determine the current authentication state
   */
  const determineAuthState = useCallback((): AuthenticationState => {
    if (authError || auth0Error) return 'error';
    if (auth0IsLoading) return 'pending_authentication';
    if (!auth0IsAuthenticated) return 'pending_authentication';
    if (unauthorizedUser) return 'unauthorized';
    if (isLoading && !user) return 'pending_authorization';
    if (auth0IsAuthenticated) return 'authenticated';
    return 'pending_authorization';
  }, [authError, auth0Error, auth0IsLoading, auth0IsAuthenticated, unauthorizedUser, isLoading, user]);

  /**
   * Load user profile after Auth0 authentication
   */
  const loadUserProfile = useCallback(async () => {
    if (!auth0IsAuthenticated || !auth0User || isLoadingProfile.current) {
      return;
    }

    try {
      isLoadingProfile.current = true;
      setIsLoading(true);
      setAuthError(null);
      setUnauthorizedUser(false);
      
      // Get access token from Auth0
      const token = await getAccessTokenSilently();
      
      // Update auth state
      setUser({ 
        sub: auth0User?.sub || '',
        email: auth0User?.email || '',
        ...auth0User
      });
      setUnauthorizedUser(false);
      
    } catch (error) {
      console.error('Failed to load user profile:', error);
      setAuthError({
        code: 'PROFILE_LOAD_ERROR',
        message: error instanceof Error ? error.message : 'Failed to load user profile'
      });
      setUnauthorizedUser(true);
      setUser(null);
    } finally {
      setIsLoading(false);
      isLoadingProfile.current = false;
    }
  }, [auth0IsAuthenticated, auth0User, getAccessTokenSilently]);

  /**
   * Effect to load user profile when Auth0 authentication completes
   */
  useEffect(() => {
    if (auth0IsAuthenticated && auth0User) {
      loadUserProfile();
    } else {
      setIsLoading(auth0IsLoading);
      setUser(null);
      setUnauthorizedUser(false);
      setAuthError(null);
    }
  }, [auth0IsAuthenticated, auth0User, auth0IsLoading, loadUserProfile]);

  /**
   * Update auth state when dependencies change
   */
  useEffect(() => {
    const newAuthState = determineAuthState();
    setAuthState(newAuthState);
  }, [determineAuthState]);

  /**
   * Handle Auth0 errors
   */
  useEffect(() => {
    if (auth0Error) {
      console.error('Auth0 error:', auth0Error);
      setAuthError({
        code: (auth0Error as any)?.error || 'AUTH0_ERROR',
        message: auth0Error.message || 'Authentication failed',
        error: (auth0Error as any)?.error,
        error_description: (auth0Error as any)?.error_description
      });
    }
  }, [auth0Error]);

  /**
   * Sign out function - clears cache and logs out of Auth0
   */
  const signOut = useCallback(async (): Promise<void> => {
    // Always clear local state first
    setUser(null);
    setUnauthorizedUser(false);
    setAuthError(null);
    sessionStorage.removeItem('userProfileExternalObjectId');
    
    // Always logout from Auth0, even if previous steps failed
    try {
      // Match working AssistPoint app syntax
      (logout as any)(logoutConfig);
    } catch (error) {
      console.error('Auth0 logout failed:', error);
      // Force redirect as last resort
      window.location.href = logoutConfig.logoutParams.returnTo;
    }
  }, [auth0IsAuthenticated, getAccessTokenSilently, logout]);


  const getUserProfileIdHeader = useCallback(() => {
    const id = sessionStorage.getItem('userProfileExternalObjectId');
    return id ? { userProfileExternalObjectId: id } : null;
  }, []);

  // Context value
  const contextValue: AuthContextValue = {
    // State
    user,
    isAuthenticated: authState === 'authenticated',
    isLoading,
    unauthorizedUser,
    // Actions
    signOut,
    getUserProfileIdHeader,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * HOC to provide auth context to class components
 */
export const withAuth = <P extends object>(
  Component: React.ComponentType<P & { auth: AuthContextValue }>
): React.FC<P> => {
  return (props: P) => {
    const auth = useAuth();
    return <Component {...props} auth={auth} />;
  };
};

/**
 * Custom hook for getting the current authentication state
 */
export const useAuthState = (): AuthenticationState => {
  const { isAuthenticated, isLoading, unauthorizedUser } = useAuth();
  
  if (isLoading) {
    return isAuthenticated ? 'pending_authorization' : 'pending_authentication';
  }
  
  if (unauthorizedUser) return 'unauthorized';
  if (isAuthenticated) return 'authenticated';
  
  return 'pending_authentication';
};

/**
 * Custom hook to get access token (wraps Auth0's getAccessTokenSilently)
 */
export const useAccessToken = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { isAuthenticated } = useAuth();
  
  const getToken = useCallback(async (): Promise<string> => {
    if (!isAuthenticated) {
      throw new Error('User is not authenticated');
    }
    
    return await getAccessTokenSilently();
  }, [isAuthenticated, getAccessTokenSilently]);
  
  return getToken;
};
